import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { getPrompt } from "@/lib/prompt-config";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Input,
	Label,
	Textarea,
	Button,
	AlertDialog,
	AlertDialogTrigger,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogAction,
	AlertDialogCancel,
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "@/components/primitives";

export default function TopicSetupPage() {
	const { topicId } = useParams<{ topicId?: string }>();
	const navigate = useNavigate();
	const { user } = useAuth();

	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [initialQuestion, setInitialQuestion] = useState("");
	const [aiPrompt, setAiPrompt] = useState("");
	const [archived, setArchived] = useState(false);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [generating, setGenerating] = useState(false);
	const [showClearConfirm, setShowClearConfirm] = useState(false);
	const [advancedOptionsOpen, setAdvancedOptionsOpen] = useState(false);
	const [savingMessage, setSavingMessage] = useState<string | null>(null);
	const conversationGuideRef = useRef<HTMLTextAreaElement>(null);
	const isEdit = !!topicId;

	useEffect(() => {
		const load = async () => {
			if (!isEdit) {
				setLoading(false);
				return;
			}
			try {
				const { data, error } = await supabase
					.from("topics")
					.select("*")
					.eq("id", topicId)
					.maybeSingle();
				if (error) {
					console.error("Error loading topic:", error);
					setLoading(false);
					return;
				}
				if (data) {
					setTitle(data.name || "");
					setDescription(data.description || "");
					setInitialQuestion(data.initial_question || "");
					setAiPrompt(data.prompt_creation || "");
					setArchived(false); // Archived field may not exist in schema
					// Trigger resize after data is loaded
					setTimeout(() => {
						const textarea = conversationGuideRef.current;
						if (textarea) {
							textarea.style.height = "auto";
							const newHeight = Math.max(150, textarea.scrollHeight);
							textarea.style.height = `${newHeight}px`;
						}
					}, 100);
				}
			} catch (err) {
				console.error(err);
			} finally {
				setLoading(false);
			}
		};
		load();
	}, [topicId]);

	// Auto-resize conversation guide textarea
	useEffect(() => {
		const textarea = conversationGuideRef.current;
		if (textarea) {
			// Reset height to auto to get the correct scrollHeight
			textarea.style.height = "auto";
			// Set height to scrollHeight, with a minimum height
			const newHeight = Math.max(150, textarea.scrollHeight);
			textarea.style.height = `${newHeight}px`;
		}
	}, [aiPrompt]);

	// Also resize when component mounts or when advanced options open
	useEffect(() => {
		if (advancedOptionsOpen && aiPrompt) {
			// Small delay to ensure DOM is ready
			setTimeout(() => {
				const textarea = conversationGuideRef.current;
				if (textarea) {
					textarea.style.height = "auto";
					const newHeight = Math.max(150, textarea.scrollHeight);
					textarea.style.height = `${newHeight}px`;
				}
			}, 0);
		}
	}, [advancedOptionsOpen, aiPrompt]);

	const generateSuggestedSetup = async () => {
		if (!title.trim()) {
			alert("Please enter a topic title first.");
			return;
		}

		// Check if fields have existing data
		const hasExistingData = initialQuestion.trim() || aiPrompt.trim();
		if (hasExistingData) {
			setShowClearConfirm(true);
			return;
		}

		await performGeneration();
	};

	const performGeneration = async (): Promise<{
		openingQuestion: string;
		conversationGuide: string;
	} | null> => {
		setGenerating(true);
		try {
			// Get the reflection_qa prompt from config
			const reflectionQAPrompt = getPrompt("reflection_qa");

			// Create a prompt for generating topic setup
			const generationPrompt = `You are helping a mentor create a reflection topic setup for a cognitive behavioral therapy reflection system.

TASK: Generate two components for a reflection topic:

1. OPENING QUESTION: A thoughtful, open-ended question (1-3 sentences) that invites mentees to explore this specific topic. The question should be personal, introspective, and help them start reflecting on their experiences related to this topic.

2. CONVERSATION GUIDE: Detailed instructions for the AI on how to conduct the reflection conversation. This should be formatted as clear, actionable guidance that a mentor can review and edit.

TOPIC INFORMATION:
Title: ${title}
${description ? `Description/Purpose: ${description}` : "No description provided"}

REFERENCE PRINCIPLES (use these as guidance for the conversation style):
${reflectionQAPrompt.substring(0, 500)}...

REQUIREMENTS:
- The opening question should be specific to this topic, not generic
- The conversation guide should include: question style, focus areas, depth level, key themes to investigate, and tone
- Format the conversation guide as structured text (bullets or numbered list) that's easy to read and edit
- Both should align with cognitive behavioral therapy principles

Generate a JSON response with this EXACT structure:
{
  "openingQuestion": "Your opening question here?",
  "conversationGuide": "Your conversation guide here with:\n- Question style: [style]\n- Focus areas: [areas]\n- Depth level: [level]\n- Key themes: [themes]\n- Tone: [tone]"
}

CRITICAL: Return ONLY valid JSON. No markdown code blocks, no explanatory text, just the JSON object.`;

			// Get session for authentication
			const {
				data: { session },
			} = await supabase.auth.getSession();
			if (!session?.access_token) {
				throw new Error("Session expired. Please sign in again.");
			}

			// Call OpenAI via ai-generate edge function
			// The function expects: promptType, userInput, and optional conversationHistory
			const { data, error } = await supabase.functions.invoke("ai-generate", {
				body: {
					promptType: "reflection_qa", // Use reflection_qa as the base prompt type
					userInput: generationPrompt,
				},
			});

			if (error) {
				throw error;
			}

			// The ai-generate function returns either:
			// - A parsed JSON object (if AI returned JSON)
			// - { response: string } (if AI returned plain text)
			if (!data) {
				throw new Error("No response from AI service");
			}

			// Handle both response formats
			let parsed;
			if (data.openingQuestion && data.conversationGuide) {
				// Already parsed JSON with our expected structure
				parsed = data;
			} else if (data.response) {
				// Plain text response, try to parse as JSON
				try {
					const responseText = data.response.trim();
					// Remove markdown code blocks if present
					const jsonMatch =
						responseText.match(/```(?:json)?\s*(\{[\s\S]*\})\s*```/) ||
						responseText.match(/(\{[\s\S]*\})/);
					const jsonText = jsonMatch ? jsonMatch[1] : responseText;
					parsed = JSON.parse(jsonText);
				} catch (parseError) {
					console.error(
						"Failed to parse AI response:",
						parseError,
						"Response was:",
						data.response?.substring(0, 500),
					);
					throw new Error(
						"AI returned invalid response format. Please try again.",
					);
				}
			} else {
				// Try to use data directly if it has our expected structure
				parsed = data;
			}

			// Validate the parsed response has the expected structure
			if (!parsed.openingQuestion && !parsed.conversationGuide) {
				console.error("AI response missing required fields:", parsed);
				throw new Error(
					"AI response missing required fields (openingQuestion or conversationGuide)",
				);
			}

			// Fill in the generated content
			const generatedOpeningQuestion = parsed.openingQuestion || "";
			const generatedConversationGuide = parsed.conversationGuide || "";

			if (generatedOpeningQuestion) {
				setInitialQuestion(generatedOpeningQuestion);
			}
			if (generatedConversationGuide) {
				setAiPrompt(generatedConversationGuide);
			}

			setShowClearConfirm(false);

			// Return the generated values for immediate use
			return {
				openingQuestion: generatedOpeningQuestion,
				conversationGuide: generatedConversationGuide,
			};
		} catch (err: any) {
			console.error("Error generating setup:", err);
			alert(
				"Failed to generate suggested setup: " +
					(err.message || "Unknown error"),
			);
			return null;
		} finally {
			setGenerating(false);
		}
	};

	const handleSave = async () => {
		if (!user?.id) return;
		if (!title.trim()) {
			alert("Title is required");
			return;
		}

		// Auto-generate if opening question and conversation guide are empty
		let finalInitialQuestion = initialQuestion;
		let finalAiPrompt = aiPrompt;

		if (!initialQuestion.trim() && !aiPrompt.trim()) {
			setSaving(true);
			setSavingMessage("Generating suggested prompts...");
			try {
				const generated = await performGeneration();
				if (generated) {
					// Use the generated values directly
					finalInitialQuestion = generated.openingQuestion;
					finalAiPrompt = generated.conversationGuide;
				}
			} catch (err: any) {
				console.error("Error auto-generating setup:", err);
				// Continue with save even if generation fails - user can save without it
			}
		}

		setSaving(true);
		setSavingMessage("Saving topic...");
		try {
			if (isEdit) {
				// try update with new columns, fallback without
				let res = await supabase
					.from("topics")
					.update({
						name: title,
						description,
						initial_question: finalInitialQuestion,
						prompt_creation: finalAiPrompt,
					})
					.eq("id", topicId)
					.eq("created_by", user.id);

				if (
					res.error &&
					/initial_question|prompt_creation/.test(res.error.message || "")
				) {
					const retry = await supabase
						.from("topics")
						.update({ name: title, description })
						.eq("id", topicId)
						.eq("created_by", user.id);
					if (retry.error) throw retry.error;
				} else if (res.error) {
					throw res.error;
				}
			} else {
				// try insert with new columns, fallback without
				let res = await supabase.from("topics").insert({
					created_by: user.id,
					name: title,
					description,
					initial_question: finalInitialQuestion,
					prompt_creation: finalAiPrompt,
				});

				if (
					res.error &&
					/initial_question|prompt_creation/.test(res.error.message || "")
				) {
					const retry = await supabase
						.from("topics")
						.insert({ created_by: user.id, name: title, description });
					if (retry.error) throw retry.error;
				} else if (res.error) {
					throw res.error;
				}
			}

			setSavingMessage(null);
			navigate("/mentor/topics");
		} catch (err: any) {
			console.error("Error saving topic:", err);
			setSavingMessage(null);
			alert("Failed to save topic: " + (err.message || err));
		} finally {
			setSaving(false);
			setSavingMessage(null);
		}
	};

	if (loading) return <div className="p-8">Loading...</div>;

	return (
		<div className="container mx-auto px-4 py-8 max-w-7xl">
			<Card>
				<CardHeader>
					<CardTitle>{isEdit ? "Edit Topic" : "Create Topic"}</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="space-y-6">
						{/* Title Field */}
						<div className="space-y-2">
							<Label htmlFor="topicTitle" className="text-base font-semibold">
								Topic Title *
							</Label>
							<Input
								id="topicTitle"
								value={title}
								onChange={(e: any) => setTitle(e.target.value)}
								placeholder="e.g., Work-Life Balance, Stress Management, Team Communication"
							/>
							<p className="text-sm text-muted-foreground">
								The topic name that mentees will see when this topic is assigned
								to them. Choose a clear, descriptive title.
							</p>
						</div>

						{/* Description Field */}
						<div className="space-y-2">
							<Label
								htmlFor="topicDescription"
								className="text-base font-semibold"
							>
								Description or Purpose
							</Label>
							<Textarea
								id="topicDescription"
								value={description}
								onChange={(e: any) => setDescription(e.target.value)}
								className="min-h-[100px]"
								placeholder="Brief overview of what this topic covers and why it's important for reflection..."
							/>
							<div className="text-xs italic text-muted-foreground">
								<p>
									This description is shown to mentees when they start a
									reflection on this topic. It helps them understand the context
									and purpose. The AI also uses this to understand the topic's
									scope when generating questions. A detailed description can
									help the AI write better prompts and generate more relevant
									opening questions and conversation guides.
								</p>
							</div>
						</div>

						{/* Advanced Options - Collapsible */}
						<div className="space-y-2">
							<Button
								type="button"
								variant="outline"
								onClick={() => setAdvancedOptionsOpen(!advancedOptionsOpen)}
								className="w-full justify-between p-3 h-auto font-semibold text-base"
							>
								<span>Advanced Options</span>
								{advancedOptionsOpen ? (
									<ChevronUp className="h-4 w-4" />
								) : (
									<ChevronDown className="h-4 w-4" />
								)}
							</Button>

							{advancedOptionsOpen && (
								<div className="space-y-6 p-4 border rounded-lg bg-muted/30">
									{/* Generate Suggested Setup Button */}
									<div className="flex justify-end">
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={generateSuggestedSetup}
											disabled={generating || !title.trim() || saving}
											className="flex items-center gap-2"
										>
											<Sparkles className="h-4 w-4" />
											{generating
												? "Generating..."
												: "Generate suggested setup"}
										</Button>
									</div>

									{/* Opening Question Field */}
									<div className="space-y-2">
										<Label
											htmlFor="topicInitial"
											className="text-base font-semibold"
										>
											Opening Question (Optional)
										</Label>
										<Textarea
											id="topicInitial"
											value={initialQuestion}
											onChange={(e: any) => setInitialQuestion(e.target.value)}
											className="min-h-[100px]"
											placeholder="e.g., 'What situations at work have been causing you the most stress recently?' or leave blank to let AI generate the first question"
										/>
										<div className="text-xs italic text-muted-foreground">
											<p className="mb-2">
												If provided, the AI will use this exact question as the
												first question in the reflection conversation. If left
												blank, the AI will generate an appropriate opening
												question based on the topic title and description.
											</p>
											<p>
												Example: "When did you first notice this pattern
												affecting your daily routine?"
											</p>
										</div>
									</div>

									{/* Conversation Guide Field */}
									<div className="space-y-2">
										<Label
											htmlFor="topicAiPrompt"
											className="text-base font-semibold"
										>
											Conversation Guide (Optional)
										</Label>
										<Textarea
											ref={conversationGuideRef}
											id="topicAiPrompt"
											value={aiPrompt}
											onChange={(e: any) => {
												setAiPrompt(e.target.value);
												// Auto-resize on input
												const textarea = e.target;
												// Reset height to get accurate scrollHeight
												textarea.style.height = "auto";
												// Calculate new height based on content
												const newHeight = Math.max(150, textarea.scrollHeight);
												textarea.style.height = `${newHeight}px`;
											}}
											className="min-h-[150px] resize-none overflow-hidden"
											placeholder={`Example:
- Focus on exploring triggers and patterns
- Ask about emotional and physical responses
- Investigate what the mentee has tried before
- Help identify concrete next steps
- Use a supportive, non-judgmental tone`}
										/>
										<div className="text-xs italic text-muted-foreground">
											<p className="mb-2">
												Provides detailed instructions to the AI on how to
												conduct the reflection conversation. You can specify
												question style, focus areas, depth level, key themes,
												and tone.
											</p>
											<p>
												Tip: Be specific about what you want the AI to explore.
												The more guidance you provide, the more tailored the
												reflection experience will be for your mentees.
											</p>
										</div>
									</div>
								</div>
							)}
						</div>

						{/* Clear Confirmation Dialog */}
						<AlertDialog
							open={showClearConfirm}
							onOpenChange={setShowClearConfirm}
						>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>Clear Existing Content?</AlertDialogTitle>
									<AlertDialogDescription>
										The Opening Question and Conversation Guide fields already
										contain content. Generating a new suggested setup will
										replace this content. Do you want to continue?
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel>Cancel</AlertDialogCancel>
									<AlertDialogAction onClick={performGeneration}>
										Yes, Generate New Setup
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>

						<div className="flex justify-between items-center">
							{isEdit ? (
								<div className="flex items-center space-x-2">
									{/* Delete Confirmation Dialog */}
									<AlertDialog>
										<AlertDialogTrigger asChild>
											<Button variant="ghost" className="text-red-600">
												Delete
											</Button>
										</AlertDialogTrigger>
										<AlertDialogContent>
											<AlertDialogHeader>
												<AlertDialogTitle>Delete Topic</AlertDialogTitle>
												<AlertDialogDescription>
													Are you sure you want to delete this topic? This
													action cannot be undone.
												</AlertDialogDescription>
											</AlertDialogHeader>
											<AlertDialogFooter>
												<AlertDialogCancel>Cancel</AlertDialogCancel>
												<AlertDialogAction
													onClick={async () => {
														try {
															if (!topicId) return;
															const { error: delAssignErr } = await supabase
																.from("topic_assignments")
																.delete()
																.eq("topic_id", topicId);
															if (delAssignErr) throw delAssignErr;
															const { error: delErr } = await supabase
																.from("topics")
																.delete()
																.eq("id", topicId)
																.eq("created_by", user?.id || "");
															if (delErr) throw delErr;
															navigate("/mentor/topics");
														} catch (err) {
															console.error("Error deleting topic:", err);
															alert("Failed to delete topic.");
														}
													}}
												>
													Delete Topic
												</AlertDialogAction>
											</AlertDialogFooter>
										</AlertDialogContent>
									</AlertDialog>

									{/* Archive/Unarchive Confirmation Dialog */}
									<AlertDialog>
										<AlertDialogTrigger asChild>
											<Button variant={archived ? "default" : "outline"}>
												{archived ? "Unarchive" : "Archive"}
											</Button>
										</AlertDialogTrigger>
										<AlertDialogContent>
											<AlertDialogHeader>
												<AlertDialogTitle>
													{archived ? "Unarchive Topic" : "Archive Topic"}
												</AlertDialogTitle>
												<AlertDialogDescription>
													{archived
														? "Unarchive this topic to make it active again."
														: "Archive this topic to hide it from active lists."}
												</AlertDialogDescription>
											</AlertDialogHeader>
											<AlertDialogFooter>
												<AlertDialogCancel>Cancel</AlertDialogCancel>
												<AlertDialogAction
													onClick={async () => {
														try {
															if (!topicId || !user?.id) return;
															// Note: archived fields may not exist in schema
															// Archive functionality disabled until schema supports it
															alert(
																"Archive functionality requires database schema update. The archived field is not currently available in the database schema.",
															);
														} catch (err) {
															console.error(
																"Error archiving/unarchiving topic:",
																err,
															);
															alert("Failed to update archive status");
														}
													}}
												>
													{archived ? "Unarchive" : "Archive"}
												</AlertDialogAction>
											</AlertDialogFooter>
										</AlertDialogContent>
									</AlertDialog>
								</div>
							) : (
								<div />
							)}

							<div className="flex items-center space-x-2">
								<Button
									variant="outline"
									onClick={() => navigate(-1)}
									disabled={saving}
								>
									Cancel
								</Button>
								<Button onClick={handleSave} disabled={saving}>
									{saving ? savingMessage || "Saving..." : "Save Topic"}
								</Button>
							</div>

							{/* Saving Status Dialog */}
							{saving && savingMessage && (
								<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
									<div className="bg-background rounded-lg border shadow-lg p-6 max-w-md w-full mx-4">
										<h3 className="text-lg font-semibold mb-2">Saving Topic</h3>
										<p className="text-sm text-muted-foreground mb-4">
											{savingMessage}
										</p>
										<div className="flex items-center justify-center py-2">
											<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
										</div>
									</div>
								</div>
							)}
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
